import _ from 'lodash';
import { createSelector } from 'reselect';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { getBorrowerFullName } from 'utils/loanApplication';
import { getApplicableAddressFieldsPendingChanges } from 'utils/pendingChanges';

import { selectLoanApplicationNonBorrowers } from './loanApplication.selectors';
import { selectEntitiesChangedPropsByIds } from './loanChangesHistory.selectors';

export const selectNonBorrowersTableData = createSelector(
  selectLoanApplicationNonBorrowers,
  loanApplicationNonBorrowers =>
    loanApplicationNonBorrowers?.map(nonBorrower => ({
      ...nonBorrower,
      fullName: getBorrowerFullName(nonBorrower.nonBorrowerFullName),
    })),
);

export const selectOrderedNonBorrowersTableData = createSelector(
  selectNonBorrowersTableData,
  nonBorrowersTableData => {
    return nonBorrowersTableData && _.orderBy(nonBorrowersTableData, ['orderNumber'], ['asc']);
  },
);

export const selectDeletedAndNewNonBorrowers = createSelector(
  selectNonBorrowersTableData,
  nonBorrowers =>
    (nonBorrowers ?? []).reduce(
      (acc, cur) => {
        if (cur.pendingChangesActionType === PendingChangesActionType.DELETED) {
          acc.deletedNonBorrowers.push(cur.id);
        }

        if (cur.pendingChangesActionType === PendingChangesActionType.ADDED) {
          acc.newNonBorrowers.push(cur.id);
        }

        return acc;
      },
      { deletedNonBorrowers: [], newNonBorrowers: [] },
    ),
);

export const selectNonBorrowersPendingChanges = createSelector(
  [selectNonBorrowersTableData, state => state],
  (nonBorrowers, state) => {
    const { nonBorrowersIds, recordsWithOuterPendingChanges } = (nonBorrowers ?? []).reduce(
      (acc, nbeData) => {
        const { id, addressId } = nbeData;

        acc.nonBorrowersIds.push(id);

        if (addressId) {
          const addressesPendingChanges = selectEntitiesChangedPropsByIds([addressId])(state);

          const { [addressId]: currentAddressPendingChanges } = addressesPendingChanges;

          if (currentAddressPendingChanges) {
            _.set(
              acc,
              `recordsWithOuterPendingChanges[${id}].address`,
              getApplicableAddressFieldsPendingChanges(currentAddressPendingChanges),
            );
          }
        }

        return acc;
      },
      { nonBorrowersIds: [], recordsWithOuterPendingChanges: {} },
    );

    const nonBorrowersPendingChanges = selectEntitiesChangedPropsByIds(nonBorrowersIds)(state);

    return _.merge(nonBorrowersPendingChanges, recordsWithOuterPendingChanges);
  },
);
